// FILE: src/blueprint/state/BlueprintProvider.jsx

import { createContext, useEffect, useMemo, useReducer } from 'react';
import { ACTIONS, blueprintReducer } from './blueprintReducer.js';
import { initialState } from './initialState.js';

const STORAGE_KEY_COACH_ID = 'dv_selected_coach_id';
const STORAGE_KEY_TYPING_MODE = 'dv_selected_typing_mode';

export const BlueprintContext = createContext({
  state: initialState,
  dispatch: () => {},
  setSelectedCoachId: () => {},
  setSelectedTypingMode: () => {},
  pilotConsent: null,
  pilotConsentAccepted: false,
});

export default function BlueprintProvider({
  children,
  initialPackId = '',
  wpApiRoot = '',
  wpNonce = '',
  showDescription = true,
  showTime = true,
  pilotConsent = null,
  pilotConsentAccepted = false,
}) {
  const [state, dispatch] = useReducer(blueprintReducer, initialState, (s) => ({
    ...s,
    packId: initialPackId || s.packId,
    selectedCoachId:
      typeof initialCoachId === 'string' && initialCoachId.trim()
        ? initialCoachId.trim()
        : s.selectedCoachId,
    selectedTypingMode:
      typeof initialTypingMode === 'string' && initialTypingMode.trim()
        ? initialTypingMode.trim()
        : s.selectedTypingMode,
    showDescription: typeof showDescription === 'boolean' ? showDescription : s.showDescription,
    showDescriptionModules:
      typeof showDescriptionModules === 'boolean'
        ? showDescriptionModules
        : s.showDescriptionModules,
    showTime: typeof showTime === 'boolean' ? showTime : s.showTime,
  }));

  useEffect(() => {
    try {
      const storedCoachId = window.localStorage.getItem(STORAGE_KEY_COACH_ID);
      const storedTypingMode = window.localStorage.getItem(STORAGE_KEY_TYPING_MODE);

      if (typeof storedCoachId === 'string' && storedCoachId.trim()) {
        dispatch({
          type: ACTIONS.SET_SELECTED_COACH,
          payload: {
            coachId: storedCoachId.trim(),
          },
        });
      }

      if (typeof storedTypingMode === 'string' && storedTypingMode.trim()) {
        dispatch({
          type: ACTIONS.SET_SELECTED_TYPING_MODE,
          payload: {
            typingMode: storedTypingMode.trim(),
          },
        });
      }
    } catch {
      // Ignore storage failures.
    }
  }, []);

  useEffect(() => {
    try {
      if (typeof state.selectedCoachId === 'string' && state.selectedCoachId.trim()) {
        window.localStorage.setItem(STORAGE_KEY_COACH_ID, state.selectedCoachId.trim());
      }
    } catch {
      // Ignore storage failures.
    }
  }, [state.selectedCoachId]);

  useEffect(() => {
    try {
      if (typeof state.selectedTypingMode === 'string' && state.selectedTypingMode.trim()) {
        window.localStorage.setItem(STORAGE_KEY_TYPING_MODE, state.selectedTypingMode.trim());
      }
    } catch {
      // Ignore storage failures.
    }
  }, [state.selectedTypingMode]);

  const setSelectedCoachId = (coachId) => {
    dispatch({
      type: ACTIONS.SET_SELECTED_COACH,
      payload: {
        coachId: typeof coachId === 'string' ? coachId.trim() : '',
      },
    });
  };

  const setSelectedTypingMode = (typingMode) => {
    dispatch({
      type: ACTIONS.SET_SELECTED_TYPING_MODE,
      payload: {
        typingMode: typeof typingMode === 'string' ? typingMode.trim() : '',
      },
    });
  };

  const value = useMemo(() => {
    return {
      state,
      dispatch,
      setSelectedCoachId,
      setSelectedTypingMode,
      pilotConsent,
      pilotConsentAccepted,
    };
  }, [state, pilotConsent, pilotConsentAccepted]);

  return <BlueprintContext.Provider value={value}>{children}</BlueprintContext.Provider>;
}
